/** @jsx jsx */

import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Button,
  Card,
  Col,
  Divider,
  Row,
  Typography,
  message,
  Modal
} from "antd";
import { jsx } from "@emotion/core";

import SingleFileUpload from "../../../components/singleFileUpload";
import CustomButton from "../../../components/button";
import useRedirect from "../../../helperHooks/useRedirect";
import useLoanStatus from "../../../hooks/auth/useLoanStatus";
import useEligibilityDetails from "../../../hooks/eligibility/useEligibilityDetails";
import useAPI from "../../../helperHooks/useAPI";

import { ServiceType } from "../../../services/ServiceType.data";
import { EligibilityService } from "../../../services/eligibilityService";
import {
  statusCardContents as CONSTANTS,
  getImageAndLabel
} from "./StatusCardConstants";
import { statusCardStyles, modalStyles } from "./StatusCardStyles";
import { ModalInputTagsStyles } from "../../../components/formHelpers/FormHelperStyles";
import { localNumber } from "../../../helper/Format";

const { Text } = Typography;

interface HomePageCardProps {
  preventDataFetch: boolean;
}
const StatusCard: React.FC<HomePageCardProps> = ({
  preventDataFetch
}: HomePageCardProps) => {
  const [receivablesModalOpen, setReceivablesModalOpen] = useState<boolean>(
    false
  );

  // Hooks for redirection and APIs.
  const redirect = useRedirect();
  const {
    subStatus,
    enableStatusCard,
    showKycReUpload,
    showAccountCreationUpload,
    showUpdatedReceivables
  } = useLoanStatus();
  const { callAPI: updateAcceptance, loading: acceptRejectLoading } = useAPI(
    EligibilityService.acceptRejectUpdatedEligibilityAmount
  );
  const {
    getEligibilityDetails,
    eligibilityState: { loading: eligibilityDetailsLoading, eligibilityDetails }
  } = useEligibilityDetails();

  //fetching eligibilityDetails if not already present.
  useEffect(() => {
    if (!eligibilityDetails && !preventDataFetch) {
      getEligibilityDetails({});
    }
  }, [getEligibilityDetails, eligibilityDetails, preventDataFetch]);

  // Component wide local variables.

  const LOADING = acceptRejectLoading || eligibilityDetailsLoading;

  const FOOTER_BUTTONS_DISABLED = !enableStatusCard || LOADING;

  // Labels and Images for Status card.
  const [kycImage, kycLabel] = getImageAndLabel(subStatus.kycStatus);
  const [accountImg, accountLabel] = getImageAndLabel(
    subStatus.disbursalBankAccountStatus
  );
  const [receivablesImg, receivablesLabel] = getImageAndLabel(
    subStatus.receivablesStatus
  );
  const {
    updatedEligibilityAmount,
    anchorGstin,
    receivableAmount,
    anchorName
  } = useMemo(() => {
    const receivablesDetails = eligibilityDetails?.receivablesDetails || [];
    return {
      receivableAmount: receivablesDetails.length
        ? receivablesDetails[0].receivableAmountForNextFourMonths || 0
        : 0,
      anchorGstin: receivablesDetails.length ? receivablesDetails[0].gstin : "",
      anchorName: receivablesDetails.length
        ? receivablesDetails[0].tradeName
        : "",
      updatedEligibilityAmount:
        eligibilityDetails?.eligibilityAmountDetails?.eligibilityAmount || 0
    };
  }, [eligibilityDetails]);

  // To avoid having to add 'disabled-text' individually in every place.
  const getClassName = (className?: string) =>
    `${className} ${!enableStatusCard ? "disabled-text" : ""}`;

  const imgClassName = useMemo(
    () => `status-image ${!enableStatusCard ? "status-image--disabled" : ""}`,
    [enableStatusCard]
  );

  const getUploadConfig = useCallback(
    () => ({
      uploadServiceType: ServiceType.Eligibility,
      uploadPath: EligibilityService.uploadDisbursalAccountProof
    }),
    []
  );

  const renderKycFooter = (smallViewPort?: boolean) => {
    const handleKycReUploadClick = () => {
      redirect(routes => routes.eligibility.kyc);
    };
    return (
      showKycReUpload && (
        <Button
          type="link"
          disabled={FOOTER_BUTTONS_DISABLED}
          className={`${smallViewPort ? "d-block d-lg-none mt-2" : "mt-5"}`}
          onClick={handleKycReUploadClick}
        >
          {CONSTANTS.buttons.reUploadKyc}
        </Button>
      )
    );
  };

  const renderAccountFooter = (smallViewPort?: boolean) => {
    return showAccountCreationUpload ? (
      <SingleFileUpload
        label={CONSTANTS.buttons.accountCreation}
        className={`flex-grow-1 ${
          smallViewPort ? "d-block d-lg-none mt-2" : "mt-5 hide-overflow"
        }`}
        uploadClassName="d-flex justify-content-center"
        disabled={FOOTER_BUTTONS_DISABLED}
        config={getUploadConfig()}
        onSuccess={() => message.success(CONSTANTS.feedback.fileUpload)}
      />
    ) : (
      <span className="flex-grow-1" />
    );
  };

  const renderReceivablesFooter = (smallViewPort?: boolean) => {
    const handleUpdate = (isAccepted: boolean) => () => {
      updateAcceptance(
        { isAccepted, anchorGstin: anchorGstin || "" },
        data => {
          if (data) {
            //refresh the eligibility data to retrieve disbursal account
            getEligibilityDetails({});
            const content = CONSTANTS.feedback.success(isAccepted);
            (isAccepted ? message.success : message.info)({ content });
          }
          setReceivablesModalOpen(false);
        },
        () => {
          setReceivablesModalOpen(false);
        }
      );
    };

    const handleModalOpen = () => {
      setReceivablesModalOpen(true);
    };
    const handleModalClose = () => {
      if (!LOADING) {
        setReceivablesModalOpen(false);
      }
    };
    return (
      showUpdatedReceivables &&
      updatedEligibilityAmount > 0 &&
      receivableAmount > 0 &&
      anchorGstin && (
        <React.Fragment>
          <Button
            className={`receivables-footer ${
              smallViewPort ? "d-block d-lg-none mt-2" : "mt-5"
            }`}
            type="link"
            onClick={handleModalOpen}
            disabled={FOOTER_BUTTONS_DISABLED}
            loading={LOADING}
          >
            {CONSTANTS.buttons.view}
          </Button>
          <Modal
            css={[ModalInputTagsStyles, modalStyles]}
            visible={receivablesModalOpen}
            onCancel={handleModalClose}
            confirmLoading={LOADING}
            title={CONSTANTS.labels.updatedReceivables}
            closable={false}
            footer={
              <section className="d-flex justify-content-center mb-3">
                <CustomButton
                  disabled={LOADING}
                  containerDivClassName="mr-3"
                  onClick={handleUpdate(false)}
                >
                  {CONSTANTS.buttons.reject}
                </CustomButton>
                <CustomButton
                  type="primary"
                  disabled={LOADING}
                  onClick={handleUpdate(true)}
                >
                  {CONSTANTS.buttons.accept}
                </CustomButton>
              </section>
            }
          >
            <section>
              <Text>{CONSTANTS.labels.receivableAmount}</Text>
              <Text className="receivable-amount">
                Rs {localNumber(receivableAmount)}
              </Text>
              {anchorName && (
                <React.Fragment>
                  <br />
                  <Text>{CONSTANTS.labels.receivablesCustomer}</Text>
                  <Text className="receivable-amount">{anchorName}.</Text>
                </React.Fragment>
              )}
              <br />
              <Text>{CONSTANTS.labels.updatedEligibilityAmount}</Text>
              <Text className="receivable-amount">
                Rs {localNumber(updatedEligibilityAmount)}
              </Text>
            </section>
          </Modal>
        </React.Fragment>
      )
    );
  };

  const renderKyc = (
    <React.Fragment>
      <Text className={getClassName("d-block d-lg-none subsection-title")}>
        {CONSTANTS.labels.kyc}
      </Text>
      <img src={kycImage} alt="Pending" className={imgClassName} />
      <Text className={getClassName("subsection-status pt-3")}>{kycLabel}</Text>
      {renderKycFooter(true)}
    </React.Fragment>
  );

  const renderAccountCreation = (
    <section className="image-and-label d-flex flex-column align-items-center flex-grow-1">
      <Text
        className={getClassName(
          "d-block d-lg-none subsection-title pt-5 flex-grow-1"
        )}
      >
        {CONSTANTS.labels.accountCreation}
      </Text>
      <img src={accountImg} alt="Pending" className={imgClassName} />
      <Text className={getClassName("subsection-status pt-3")}>
        {accountLabel}
      </Text>
      {renderAccountFooter(true)}
    </section>
  );

  const renderReceivablesConfimation = (
    <React.Fragment>
      <Text className={getClassName("d-block d-lg-none pt-5 subsection-title")}>
        {CONSTANTS.labels.receivables}
      </Text>
      <img src={receivablesImg} alt="Pending" className={imgClassName} />
      <Text className={getClassName("subsection-status pt-3")}>
        {receivablesLabel}
      </Text>
      {renderReceivablesFooter(true)}
    </React.Fragment>
  );

  return (
    <Card bordered={false} loading={LOADING} css={statusCardStyles}>
      <Text className={getClassName("home-card-title d-block pb-4")}>
        {CONSTANTS.labels.status}
      </Text>
      <Row type="flex" className="d-none d-lg-flex">
        <Col xs={24} lg={8} className="d-flex justify-content-center">
          <Text className={getClassName("subsection-title")}>
            {CONSTANTS.labels.kyc}
          </Text>
        </Col>
        <Col xs={24} lg={9} className="d-flex">
          <Divider type="vertical" dashed className="d-none d-lg-block" />
          <Text className={getClassName("subsection-title pb-5 flex-grow-1")}>
            {CONSTANTS.labels.accountCreation}
          </Text>
          <Divider type="vertical" dashed className="d-none d-lg-block" />
        </Col>
        <Col xs={24} lg={7} className="d-flex justify-content-center">
          <Text className={getClassName("subsection-title")}>
            {CONSTANTS.labels.receivables}
          </Text>
        </Col>
      </Row>
      <Row type="flex">
        <Col
          xs={24}
          lg={8}
          className="d-flex flex-column image-and-label align-items-center"
        >
          {renderKyc}
        </Col>
        <Col xs={24} lg={9} className="d-flex">
          <Divider type="vertical" dashed className="d-none d-lg-block" />
          {renderAccountCreation}
          <Divider type="vertical" dashed className="d-none d-lg-block" />
        </Col>
        <Col
          xs={24}
          lg={7}
          className="d-flex flex-column image-and-label align-items-center"
        >
          {renderReceivablesConfimation}
        </Col>
      </Row>
      <Row type="flex" className="d-none d-lg-flex">
        <Col xs={24} lg={8} className="d-flex justify-content-center">
          {renderKycFooter()}
        </Col>
        <Col xs={24} lg={9} className="d-flex">
          <Divider type="vertical" dashed className="d-none d-lg-block" />
          {renderAccountFooter()}
          <Divider type="vertical" dashed className="d-none d-lg-block" />
        </Col>
        <Col xs={24} lg={7} className="d-flex justify-content-center">
          {renderReceivablesFooter()}
        </Col>
      </Row>
    </Card>
  );
};

export default StatusCard;
